import React, { useState, useEffect } from 'react';
import { useGoldPrice } from './hooks/useGoldPrice';
import PriceCard from './components/PriceCard';
import GoldChart from './components/GoldChart';
import { 
  Globe, Clock, ArrowRightLeft, ChartLine, TrendingUp , Mail
} from 'lucide-react';
import logo from './assets/logo.png'; // غير الاسم لاسم ملفك
function App() {
  const prices = useGoldPrice();
  const [view, setView] = useState('home');
  // حالات المحول
  const [amount, setAmount] = useState(1);
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("EGP");
  const [conversionResult, setConversionResult] = useState(0);

  // تحديث المحول ديناميكياً من الـ API
useEffect(() => {
    // نجهز أسعار الصرف ونضيف USD بقيمة 1 لو مش موجود
    const rates = { ...prices.rates, USD: 1 };
    
    if (rates[fromCurrency] && rates[toCurrency]) {
      const fromRate = parseFloat(rates[fromCurrency]);
      const toRate = parseFloat(rates[toCurrency]);
      const calculated = (parseFloat(amount) / fromRate) * toRate;
      
      if (!isNaN(calculated)) {
        setConversionResult(calculated.toLocaleString(undefined, { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }));
      }
    }
  }, [amount, fromCurrency, toCurrency, prices.rates]);

  // تعريف العملات للمربعات الـ 10 (بناءً على طلبك بالترتيب)
  const boxConfigs = [
    { pair: "EUR/USD", key: "EUR", sign: "€", invert: true },
    { pair: "GBP/USD", key: "GBP", sign: "£", invert: true },
    { pair: "USD/JPY", key: "JPY", sign: "¥", invert: false },
    { pair: "USD/AUD", key: "AUD", sign: "A$", invert: false },
    { pair: "USD/CAD", key: "CAD", sign: "C$", invert: false },
    { pair: "CHF/USD", key: "CHF", sign: "Fr", invert: true },
    { pair: "USD/CNY", key: "CNY", sign: "¥", invert: false },
    { pair: "USD/INR", key: "INR", sign: "₹", invert: false },
    { pair: "JOD/USD", key: "JOD", sign: "د.ا", invert: true },
    { pair: "USD/ILS", key: "ILS", sign: "₪", invert: false }
  ];
// 1. استخراج القيم الأساسية من الـ API
  const globalGoldPrice = prices.gold || 0; // سعر الأونصة العالمي
  const usdToEgp = prices.rates['EGP'] || 48.50; // سعر الدولار مقابل الجنيه

  // 2. حساب سعر جرام 24 (الأساس)
  const gram24 = (globalGoldPrice / 31.1035) * usdToEgp;

  // 3. استنتاج باقي العيارات بناءً على نقاء الذهب
  const egPrices = {
    24: {
      buy: (gram24 * 0.99).toFixed(0),  // خصم 1% للهامش
      sell: (gram24 * 1.01).toFixed(0)  // إضافة 1% هامش
    },
    21: {
      buy: (gram24 * (21/24) * 0.99).toFixed(0),
      sell: (gram24 * (21/24) * 1.01).toFixed(0)
    },
    18: {
      buy: (gram24 * (18/24) * 0.99).toFixed(0),
      sell: (gram24 * (18/24) * 1.01).toFixed(0)
    }
  };
  return (
    <div className="min-h-screen bg-background text-foreground bg-grid-pattern font-sans" dir="rtl">
      
      {/* 1. Ticker العلوي - 20 عملة لايف من الـ API */}
      <div className="overflow-hidden bg-muted/30 border-y border-border/40 py-2 w-full">
        <div className="flex animate-scroll-left w-max">
          {(prices.forex.length > 0 ? prices.forex : []).concat(prices.forex).concat(prices.forex).map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-mono px-6 shrink-0 border-l border-border/10">
              <span className="text-muted-foreground uppercase">{c.pair}</span>
              <span className="text-foreground font-bold">{c.val}</span>
              <span className={c.up ? "text-green-500" : "text-red-500"}>▲</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

{/* الهيدر المطور - متوافق مع الموبايل ليظهر في المنتصف */}
{/* الهيدر الذكي: في النص للموبايل وعلى الأطراف للديسكتوب */}
<header className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-6 py-8 border-border/40 mb-0 max-w-7xl mx-auto px-4">
  
  {/* اللوجو - الآن في المنتصف دائماً على الموبايل */}
  <a href="/" className="block transition-transform hover:scale-105 active:scale-95">
    <img 
      src={logo} 
      alt="GoldPulse Logo" 
      className="h-12 md:h-14 w-auto object-contain" 
    />
  </a>

  {/* حاوية الزرار والوقت */}
  <div className="flex flex-col items-center md:items-end gap-4 sm:flex-row md:gap-6">
    {/* زر تحميل التطبيق */}
    <a href="https://play.google.com/store/apps/details?id=com.apkgon.goldpulse&hl=ar" 
	  target="_blank" rel="noreferrer"
      className="group relative flex items-center gap-3 px-6 py-2.5 bg-black border border-primary/30 rounded-2xl hover:border-primary transition-all duration-500 shadow-[0_0_15px_rgba(234,179,8,0.05)] hover:shadow-[0_0_20px_rgba(234,179,8,0.15)]"
    >
      <div className="flex flex-col items-end">
        <span className="text-[9px] font-bold text-muted-foreground/60 uppercase tracking-widest leading-none">حمل التطبيق الأن من</span>
        <span className="text-sm font-black text-white tracking-tight">Google Play</span>
      </div>
      
      <div className="text-primary group-hover:scale-110 transition-transform duration-500">
        <svg fill="currentColor" height="28" viewBox="0 0 512 512" width="28" xmlns="http://www.w3.org/2000/svg">
          <path d="M48 59.49v393a4.33 4.33 0 007.37 3.07L260 256 55.37 56.42A4.33 4.33 0 0048 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7.06l201.13 192.32zM84.08 472.39c-3.64 3.48.56 9.46 5 7.06l.16-.09L345.8 338l-60.61-57.95zM449.38 231l-71.65-39.46L310.36 256l67.37 64.43L449.38 281c19.49-10.77 19.49-39.23 0-50z"></path>
        </svg>
      </div>
    </a>

    {/* سطر الوقت - يظهر تحت الزرار في الموبايل أو يختفي حسب رغبتك */}
    <div className="flex flex-col items-center">
      <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span> تحديث البورصة المباشر
      </p>
<p className="font-mono text-sm font-bold text-primary">{new Date().toLocaleTimeString('ar-EG')}</p>
    </div>
  </div>
</header>
{/* المنيو الاحترافي الجديد - Sharp Glass Style */}
{/* المنيو الاحترافي - Sharp Glass Style */}
<nav className="flex flex-wrap items-center justify-center md:justify-start gap-2 py-2 mb-6 font-sans border-t border-white/[0.05]">
  {[
    { id: 'home', label: 'الرئيسية' },
    { id: 'gold', label: 'أسعار الذهب' },
    { id: 'silver', label: 'أسعار الفضة' },
    { id: 'blog', label: 'المدونة', isExternal: true, url: 'https://blog.goldpulse.org' },
    { id: 'risk', label: 'تحذير المخاطر' },
    { id: 'contact', label: 'اتصل بنا' }
  ].map((item) => {
    // 1. تعريف الشكل (الستايل) الموحد عشان اللينك والزرار يبقوا زي بعض بالظبط
    const baseStyle = `px-5 py-2.5 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all duration-300 border border-white/5 
      ${view === item.id 
        ? 'bg-white text-black shadow-xl scale-105' 
        : 'bg-white/[0.03] text-muted-foreground backdrop-blur-md hover:bg-white/[0.08] hover:text-white'
      }`;

    // 2. لو العنصر لينك خارجي (المدونة)
    if (item.isExternal) {
      return (
        <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className={baseStyle}>
          {item.label}
        </a>
      );
    }

    // 3. لو العنصر زرار داخلي (باقي الصفحات)
    return (
      <button
        key={item.id}
        onClick={() => {
          setView(item.id);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className={baseStyle}
      >
        {item.label}
      </button>
    );
  })}
</nav>

{view === 'home' && (
  <>
    {/* 2. الذهب والفضة (لايف من الـ API بقيمة 4700+) */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
   <PriceCard 
      type="gold" 
      title="سعر الذهب العالمي" 
      price={prices.gold} 
      change={prices.change} 
      amount={prices.amount} 
      high={prices.goldHigh} 
      low={prices.goldLow} 
   />
   <PriceCard 
      type="silver" 
      title="سعر الفضة العالمي" 
      price={prices.silver} 
      change="-1.20%" 
      amount="$0.35" 
      high={prices.silverHigh} 
      low={prices.silverLow} 
   />
</div>

{/* 2.5 Egyptian Market Prices Bar - DYNAMIC */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
  {[
    { label: "عيار 24", buy: egPrices[24].buy, sell: egPrices[24].sell, color: "text-primary" },
    { label: "عيار 21", buy: egPrices[21].buy, sell: egPrices[21].sell, color: "text-white" },
    { label: "عيار 18", buy: egPrices[18].buy, sell: egPrices[18].sell, color: "text-white/80" },
    { label: "سعر الدولار", buy: (usdToEgp - 0.15).toFixed(2), sell: usdToEgp.toFixed(2), color: "text-green-500" }
  ].map((item, index) => (
    <div key={index} className="bg-[#111] border border-border/60 rounded-[24px] p-6 shadow-2xl hover:border-primary/30 transition-all flex flex-col justify-between min-h-[140px]">
      
      <div className="flex justify-between items-start mb-4">
        <span className={`text-sm md:text-base font-black uppercase tracking-tighter ${item.color}`}>
          {item.label}
        </span>
        <div className="flex gap-1.5 mt-1">
           <span className="w-1.5 h-1.5 rounded-full bg-green-500/50 animate-pulse"></span>
        </div>
      </div>
      
      <div className="flex justify-between items-end gap-2">
        <div className="flex-1 space-y-1">
          <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">شراء</p>
          <p className="font-mono font-bold text-lg md:text-xl text-white/90">{item.buy}</p>
        </div>

        <div className="h-10 w-[1px] bg-gradient-to-b from-transparent via-border/40 to-transparent"></div>

        <div className="flex-1 space-y-1 text-left">
          <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">بيع</p>
          <p className="font-mono font-black text-2xl md:text-3xl text-primary leading-none tracking-tighter">{item.sell}</p>
        </div>
      </div>
    </div>
  ))}
</div>
{/* 5.5 ADVERTISEMENT SPACE (Height: 90px) */}
<div className="max-w-7xl mx-auto px-4 mb-10">
    <div className="relative group cursor-pointer">
        {/* تأثير الوهج الخارجي */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/10 via-border/5 to-primary/10 rounded-xl blur opacity-20 group-hover:opacity-50 transition duration-700"></div>
        
        <div className="relative flex items-center justify-center h-[90px] bg-[#0a0a0a] border border-border/30 rounded-xl overflow-hidden shadow-inner">
            {/* كلمة ADVERTISEMENT في الخلفية */}
            <span className="absolute top-1.5 text-[7px] font-black uppercase tracking-[1em] text-muted-foreground/20 italic">
                ADVERTISEMENT
            </span>
            
            {/* المحتوى الرئيسي في سطر واحد ليتناسب مع الارتفاع */}
            <div className="flex items-center gap-4">

                
                <h3 className="text-sm md:text-lg font-black italic tracking-tight text-muted-foreground/40 uppercase">
                    Ad space available <span className="text-primary/20 ml-1">On Goldpulse</span>
                </h3>

                <div className="hidden md:block h-4 w-px bg-border/20 mx-2"></div>
                
                <div className="h-8 w-8 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center">
                    <Globe size={16} className="text-primary/30" />
                </div>
            </div>

            {/* خطوط الديكور الجانبية */}
            <div className="absolute left-0 h-full w-[2px] bg-primary/10"></div>
            <div className="absolute right-0 h-full w-[2px] bg-primary/10"></div>
        </div>
    </div>
</div>
{/* 3. الرسوم البيانية - مع الأيقونة والنبض الضوئي */}
<div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
        {/* أيقونة الرسم البياني باللون الذهبي */}
        <ChartLine size={24} className="text-primary" />
        
        <h2 className="text-xl md:text-2xl font-black italic tracking-tighter text-white uppercase">
            الرسوم البيانية
        </h2>
        
        {/* خط ديكوري صغير يمتد بعد العنوان */}
        <div className="h-px flex-1 bg-gradient-to-r from-border/40 to-transparent mr-4"></div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-border/40 rounded-[24px] p-2 h-[450px] shadow-2xl overflow-hidden group hover:border-primary/20 transition-all">
            <GoldChart symbol="OANDA:XAUUSD" />
        </div>
        <div className="bg-[#0a0a0a] border border-border/40 rounded-[24px] p-2 h-[450px] shadow-2xl overflow-hidden group hover:border-primary/20 transition-all">
            <GoldChart symbol="OANDA:XAGUSD" />
        </div>
    </div>
</div>

{/* 4. شبكة العملات الـ 10 - العنوان مع الأيقونة والنبض */}
<div className="mb-12">
  <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
    <div className="space-y-1">
      {/* السطر الرئيسي: الأيقونة + العنوان + مباشر */}
      <div className="flex items-center gap-3">
        {/* الأيقونة قبل الكلمة */}
        <Globe size={22} className="text-primary" />
        
        <h3 className="text-xl md:text-1xl font-black italic tracking-tighter text-white uppercase flex items-center">
          أسعار صرف العملات 
          <span className="relative mr-3 inline-flex items-center">
            {/* كلمة مباشر */}
            <span className="text-green-500 ml-2">مباشر</span>
            {/* الدائرة المتوهجة */}
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 shadow-[0_0_10px_#22c55e]"></span>
            </span>
			      {/* سطر الأساس */}
      <p className="text-[10px] font-bold text-muted-foreground/40 uppercase tracking-[0.3em] flex items-center gap-2 pr-9">
        الأساس: 1 USD
      </p>
          </span>
        </h3>
      </div>
    </div>
    
    {/* API Status */}
    <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full border border-white/5 bg-white/[0.02]">
      <div className="w-1 h-1 rounded-full bg-green-500"></div>
      <span className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-widest">Price Status: Stable</span>
    </div>
  </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {boxConfigs.map((box, i) => {
              const rawRate = prices.rates[box.key];
              const displayVal = rawRate ? (box.invert ? (1 / rawRate).toFixed(4) : rawRate.toFixed(4)) : "---";
              return (
                <div key={i} className="bg-card border border-border rounded-xl p-5 hover:border-primary/50 transition-all shadow-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] text-muted-foreground font-bold uppercase">{box.pair}</span>
                    <TrendingUp size={12} className="text-green-500" />
                  </div>
                  <div className="text-xl font-bold font-mono tracking-tighter">{box.sign}{displayVal}</div>
                  <div className="text-[9px] text-muted-foreground mt-1">1 USD = {displayVal} {box.key}</div>
                </div>
              );
            })}
          </div>
        </div>

{/* 5. ADVANCED DYNAMIC CONVERTER - FIXED USD VISIBILITY */}
<div className="bg-[#111] border border-border/60 rounded-[32px] p-8 md:p-12 mb-12 shadow-2xl relative overflow-hidden">
    <h2 className="text-2xl font-bold mb-10 text-center text-white italic tracking-tighter">محول العملات الذكي</h2>

    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-4 items-end gap-6 font-mono">
      
      {/* 1. المبلغ */}
      <div className="space-y-3 text-right">
        <label className="text-[10px] font-bold text-primary uppercase px-1 tracking-[0.2em]">المبلغ</label>
        <input 
          type="number" 
          value={amount} 
          onChange={(e) => setAmount(e.target.value)} 
          className="w-full bg-[#0a0a0a] border border-border/50 h-14 rounded-2xl px-6 text-xl text-white outline-none focus:border-primary shadow-inner font-bold" 
        />
      </div>
      
      {/* 2. من عملة */}
      <div className="space-y-3 text-right">
        <label className="text-[10px] font-bold text-muted-foreground uppercase px-1 tracking-widest">من عملة</label>
        <select 
          value={fromCurrency} 
          onChange={(e) => setFromCurrency(e.target.value)} 
          className="w-full bg-[#0a0a0a] border border-border/50 h-14 rounded-2xl px-4 text-white outline-none font-bold text-sm cursor-pointer hover:border-primary transition-all"
          style={{ colorScheme: 'dark' }}
        >
          {/* تأكدنا هنا من إضافة USD يدوياً حتى لو الـ API مبعتهاش */}
          {["USD", "EGP", "EUR", "GBP", "SAR", "AED", "KWD", "JOD", "QAR", "BHD", "OMR", "TRY", "CAD"].map(curr => {
            // نظهر العملة إذا كانت USD أو إذا كانت موجودة في الـ API
            if (curr === "USD" || (prices.rates && prices.rates[curr])) {
              return <option key={curr} value={curr} className="bg-[#0a0a0a] text-white">{curr}</option>;
            }
            return null;
          })}
        </select>
      </div>

      {/* 3. إلى عملة */}
      <div className="space-y-3 text-right">
        <label className="text-[10px] font-bold text-muted-foreground uppercase px-1 tracking-widest">إلى عملة</label>
        <select 
          value={toCurrency} 
          onChange={(e) => setToCurrency(e.target.value)} 
          className="w-full bg-[#0a0a0a] border border-border/50 h-14 rounded-2xl px-4 text-white outline-none font-bold text-sm cursor-pointer hover:border-primary transition-all"
          style={{ colorScheme: 'dark' }}
        >
          {["EGP", "USD", "EUR", "GBP", "SAR", "AED", "KWD", "JOD", "QAR", "BHD", "OMR", "TRY", "CAD"].map(curr => {
            if (curr === "USD" || (prices.rates && prices.rates[curr])) {
              return <option key={curr} value={curr} className="bg-[#0a0a0a] text-white">{curr}</option>;
            }
            return null;
          })}
        </select>
      </div>

      {/* 4. النتيجة */}
      <div className="relative group">
        <div className="bg-primary/5 border border-primary/20 h-14 rounded-2xl flex items-center justify-between px-6 font-black text-primary text-2xl border-dashed">
           <span className="truncate">{conversionResult}</span>
           <span className="text-[10px] opacity-60 mr-2 uppercase">{toCurrency}</span>
        </div>
      </div>
    </div>
</div>

{/* 5.5 ADVERTISEMENT SPACE (Height: 90px) */}
<div className="max-w-7xl mx-auto px-4 mb-10">
    <div className="relative group cursor-pointer">
        {/* تأثير الوهج الخارجي */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/10 via-border/5 to-primary/10 rounded-xl blur opacity-20 group-hover:opacity-50 transition duration-700"></div>
        
        <div className="relative flex items-center justify-center h-[90px] bg-[#0a0a0a] border border-border/30 rounded-xl overflow-hidden shadow-inner">
            {/* كلمة ADVERTISEMENT في الخلفية */}
            <span className="absolute top-1.5 text-[7px] font-black uppercase tracking-[1em] text-muted-foreground/20 italic">
                ADVERTISEMENT
            </span>
            
            {/* المحتوى الرئيسي في سطر واحد ليتناسب مع الارتفاع */}
            <div className="flex items-center gap-4">

                
                <h3 className="text-sm md:text-lg font-black italic tracking-tight text-muted-foreground/40 uppercase">
                    Ad space available <span className="text-primary/20 ml-1">On Goldpulse</span>
                </h3>

                <div className="hidden md:block h-4 w-px bg-border/20 mx-2"></div>
                
                <div className="h-8 w-8 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center">
                    <Globe size={16} className="text-primary/30" />
                </div>
            </div>

            {/* خطوط الديكور الجانبية */}
            <div className="absolute left-0 h-full w-[2px] bg-primary/10"></div>
            <div className="absolute right-0 h-full w-[2px] bg-primary/10"></div>
        </div>
    </div>
</div>

{/* شريط العملات المتحرك - خلفية بيضاء شفافة احترافية */}
<div className="mt-8 overflow-hidden bg-white/[0.03] backdrop-blur-sm border-y border-white/[0.05] py-3 w-full">
  <div className="flex animate-scroll-left w-max">
    {[...prices.forex, ...prices.forex, ...prices.forex].map((c, i) => (
      <div key={i} className="flex items-center gap-3 text-[11px] font-mono px-8 shrink-0 border-l border-white/[0.05]">
        <span className="text-muted-foreground/50 uppercase font-bold italic">
          {c.pair}
        </span>
        <span className="text-white font-black tracking-tighter">
          {c.val}
        </span>
        <span className={c.up ? "text-green-500/60" : "text-red-500/60"}>
          {c.up ? "▲" : "▼"}
        </span>
      </div>
    ))}
  </div>
</div>
</>
)}
{view === 'risk' && (
/* محتوى صفحة تحذير المخاطر المحدث - Sharp Glass Style */
<div className="py-6 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full" dir="rtl">
  <div className="max-w-4xl mx-auto space-y-10 px-4">
    
    {/* العنوان الرئيسي - محاذي لليمين */}
    <div className="border-r-4 border-primary pr-8 mb-16 text-right">
      <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
        تحذير <span className="text-primary">المخاطر</span>
      </h1>
      <p className="text-muted-foreground/60 text-[10px] font-black mt-3 uppercase tracking-[0.5em]">إخلاء مسؤولية وتحذير مخاطر الاستثمار</p>
    </div>

    <div className="grid gap-4 w-full">
      {[
        { 
          title: "مخاطر التداول", 
          text: "ينطوي تداول المعادن الثمينة، بما في ذلك الذهب والفضة، على مخاطر كبيرة للخسارة وهو غير مناسب لجميع المستثمرين. يمكن أن تتقلب قيمة المعادن الثمينة بشكل كبير بناءً على ظروف السوق والعوامل الاقتصادية والأحداث الجيوسياسية." 
        },
        { 
          title: "تقلبات السوق", 
          text: "يمكن أن تكون أسواق المعادن الثمينة متقلبة للغاية. يمكن أن تتغير الأسعار بسرعة وبشكل غير متوقع، مما يعني أنك قد تخسر بعض أو كل استثمارك. الأداء السابق لا يشير إلى النتائج المستقبلية." 
        },
        { 
          title: "تحذير الرافعة المالية", 
          text: "إذا تداولت المعادن الثمينة باستخدام الرافعة المالية أو الهامش، فقد تخسر أكثر من استثمارك الأولي. تضخّم الرافعة المالية كلاً من المكاسب والخسائر المحتملة." 
        },
        { 
          title: "لا ضمان للربح", 
          text: "لا يوجد ضمان بأن أي استراتيجية استثمار أو إشارات تداول أو تحليل مقدم على هذه المنصة سيؤدي إلى أرباح. جميع قرارات التداول هي مسؤوليتك الشخصية بالكامل." 
        },
        { 
          title: "اطلب المشورة المهنية", 
          text: "قبل اتخاذ أي قرارات استثمارية، نوصي بشدة باستشارة مستشار مالي مؤهل يمكنه تقييم ظروفك الفردية وتحمّلك للمخاطر وأهدافك الاستثمارية." 
        }
      ].map((item, idx) => (
        <div key={idx} className="bg-white/[0.02] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.04] transition-all duration-300 text-right">
          <h3 className="text-primary font-black italic text-lg mb-3 uppercase tracking-tight">{item.title}</h3>
          <p className="text-muted-foreground/80 leading-relaxed text-base md:text-lg">{item.text}</p>
        </div>
      ))}
    </div>

    {/* صندوق التنبيه الهام - محاذي لليمين */}
    <div className="mt-12 bg-white/[0.03] border border-white/20 p-8 rounded-2xl backdrop-blur-md relative overflow-hidden text-right">
      <div className="absolute top-0 right-0 w-1 h-full bg-white/40"></div>
      <p className="text-white text-sm md:text-base leading-relaxed font-bold">
        <span className="text-primary italic ml-2">هام:</span>
        المعلومات المقدمة على GoldPulse هي لأغراض إعلامية فقط ولا تشكل نصيحة مالية أو استثمارية أو تداولية. هذه ليست نصيحة مالية.
      </p>
    </div>

    {/* زر العودة - محاذي لليمين */}
    <div className="pt-10 flex justify-end">
      <button 
        onClick={() => { setView('home'); window.scrollTo(0,0); }}
        className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.3em] hover:text-white transition-all"
      >
        <span>العودة للرئيسية</span>
        <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
      </button>
    </div>
  </div>
</div>
)}
{/* محتوى صفحة سياسة الخصوصية - مترجمة واحترافية */}
{view === 'privacy' && (
  <div className="py-10 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full" dir="rtl">
    <div className="max-w-4xl mx-auto space-y-10 px-4 text-right">
      
      {/* العنوان الرئيسي */}
      <div className="border-r-4 border-primary pr-8 mb-16">
        <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
          سياسة <span className="text-primary">الخصوصية</span>
        </h1>
        <p className="text-muted-foreground/60 text-[10px] font-black mt-3 uppercase tracking-[0.5em]">سياسة الخصوصية وحماية البيانات</p>
      </div>

      <div className="grid gap-4">
        {[
          { 
            title: "المعلومات التي نجمعها", 
            text: "تم تصميم GoldPulse مع مراعاة الخصوصية. نحن نجمع الحد الأدنى من المعلومات اللازمة لتقديم خدمتنا: بيانات الاستخدام (الصفحات المزارة، الميزات المستخدمة)، معلومات الجهاز (نوع المتصفح، حجم الشاشة)، وعنوان IP (لأغراض التحليل فقط)." 
          },
          { 
            title: "كيفية استخدام معلوماتك", 
            text: "نستخدم المعلومات لتقديم الخدمة وصيانتها، تحسين تجربة المستخدم، تحليل أنماط الاستخدام، وعرض الإعلانات ذات الصلة." 
          },
          { 
            title: "ملفات تعريف الارتباط (Cookies)", 
            text: "نستخدم ملفات تعريف الارتباط والتقنيات المشابهة لتتبع النشاط وتخزين معلومات معينة. يمكنك توجيه متصفحك لرفض جميع ملفات تعريف الارتباط أو الإشارة إلى وقت إرسالها." 
          },
          { 
            title: "خدمات الطرف الثالث", 
            text: "نستخدم خدمات خارجية قد تجمع معلومات عنك: Google AdSense للإعلانات، مزودي التحليلات لإحصائيات الاستخدام، ومزودي بيانات الأسعار لمعلومات السوق." 
          },
          { 
            title: "أمن البيانات", 
            text: "نطبق تدابير أمنية مناسبة لحماية معلوماتك. ومع ذلك، لا توجد وسيلة نقل عبر الإنترنت آمنة بنسبة 100%، ولا يمكننا ضمان الأمان المطلق." 
          },
          { 
            title: "حقوقك", 
            text: "بناءً على موقعك، قد يكون لديك حقوق معينة فيما يتعلق ببياناتك الشخصية، بما في ذلك الحق في الوصول إلى معلوماتك أو تصحيحها أو حذفها. اتصل بنا لممارسة هذه الحقوق." 
          }
        ].map((item, idx) => (
          <div key={idx} className="bg-white/[0.02] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.04] transition-all duration-300">
            <h3 className="text-primary font-black italic text-lg mb-3 uppercase tracking-tight">{item.title}</h3>
            <p className="text-muted-foreground/80 leading-relaxed text-base md:text-lg">{item.text}</p>
          </div>
        ))}
      </div>

      {/* تنبيه التحديث */}
      <div className="mt-8 p-6 bg-white/[0.01] border border-white/5 rounded-2xl text-[11px] text-muted-foreground italic">
        آخر تحديث لسياسة الخصوصية: أبريل 2026. قد نقوم بتحديث هذه السياسة من وقت لآخر.
      </div>

      {/* زر العودة */}
      <div className="pt-10 flex justify-end">
        <button 
          onClick={() => { setView('home'); window.scrollTo({top:0, behavior:'smooth'}); }}
          className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.3em] hover:text-white transition-all"
        >
          <span>العودة للرئيسية</span>
          <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
)}
{/* محتوى صفحة إخلاء المسؤولية - Disclaimer - مترجمة واحترافية */}
{view === 'disclaimer' && (
  <div className="py-10 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full" dir="rtl">
    <div className="max-w-4xl mx-auto space-y-10 px-4 text-right">
      
      {/* العنوان الرئيسي */}
      <div className="border-r-4 border-primary pr-8 mb-16 text-right">
        <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
          إخلاء المسئولية
        </h1>
        <p className="text-muted-foreground/60 text-[10px] font-black mt-3 uppercase tracking-[0.5em]">إخلاء المسؤولية القانونية</p>
      </div>

      <div className="grid gap-4 w-full">
        {[
          { 
            title: "معلومات عامة", 
            text: "المعلومات المقدمة على GoldPulse هي لأغراض إعلامية عامة فقط. يتم تقديم جميع المعلومات على الموقع بحسن نية، ومع ذلك فإننا لا نقدم أي تعهد أو ضمان من أي نوع، صريحاً أو ضمنياً، فيما يتعلق بدقة أو كفاية أو صلاحية أو موثوقية أو توفر أو اكتمال أي معلومات على الموقع." 
          },
          { 
            title: "ليست نصيحة مالية", 
            text: "المحتوى الموجود على هذا الموقع، بما في ذلك على سبيل المثال لا الحصر بيانات الأسعار، والتحليل الفني، وتوقعات الذكاء الاصطناعي، واتجاهات السوق، واستجابات الدردشة، لا يشكل نصيحة مالية أو استثمارية أو تداولية. لا ينبغي عليك التعامل مع أي من محتوى الموقع على هذا النحو." 
          },
          { 
            title: "المحتوى المُنشأ بواسطة الذكاء الاصطناعي", 
            text: "يتم إنشاء بعض المحتويات على هذه المنصة باستخدام الذكاء الاصطناعي. التوقعات وتحليلات المشاعر وملخصات الأخبار الناتجة عن الذكاء الاصطناعي هي لأغراض معلوماتية فقط ولا ينبغي الاعتماد عليها لاتخاذ قرارات مالية. يمكن لأنظمة الذكاء الاصطناعي ارتكاب أخطاء وقد لا تأخذ في الاعتبار جميع العوامل ذات الصلة." 
          },
          { 
            title: "بيانات الطرف الثالث", 
            text: "يتم الحصول على بيانات الأسعار وأسعار الصرف من مقدمي خدمات خارجيين. بينما نسعى جاهدين لعرض معلومات دقيقة ومحدثة، لا يمكننا ضمان دقة أو توقيت هذه البيانات. قد تكون هناك تأخيرات أو اختلافات بين الأسعار المعروضة لدينا وأسعار السوق الفعلية." 
          },
          { 
            title: "تحديد المسؤولية", 
            text: "تحت أي ظرف من الظروف، لن نتحمل أي مسؤولية تجاهك عن أي خسارة أو ضرر من أي نوع يحدث نتيجة لاستخدام الموقع أو الاعتماد على أي معلومات مقدمة فيه. إن استخدامك للموقع واعتمادك على أي معلومة فيه يكون على مسؤوليتك الخاصة فقط." 
          }
        ].map((item, idx) => (
          <div key={idx} className="bg-white/[0.02] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.04] transition-all duration-300 text-right">
            <h3 className="text-primary font-black italic text-lg mb-3 uppercase tracking-tight">{item.title}</h3>
            <p className="text-muted-foreground/80 leading-relaxed text-base md:text-lg">{item.text}</p>
          </div>
        ))}
      </div>

      {/* زر العودة للرئيسية */}
      <div className="pt-10 flex justify-end">
        <button 
          onClick={() => { setView('home'); window.scrollTo({top:0, behavior:'smooth'}); }}
          className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.3em] hover:text-white transition-all"
        >
          <span>العودة للرئيسية</span>
          <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
)}
{/* محتوى صفحة شروط الخدمة - Terms of Service - مترجمة واحترافية */}
{view === 'terms' && (
  <div className="py-10 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full" dir="rtl">
    <div className="max-w-4xl mx-auto space-y-10 px-4 text-right">
      
      {/* العنوان الرئيسي */}
      <div className="border-r-4 border-primary pr-8 mb-16 text-right">
        <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
          شروط <span className="text-primary">الخدمة</span>
        </h1>
        <p className="text-muted-foreground/60 text-[10px] font-black mt-3 uppercase tracking-[0.5em]">شروط وأحكام الخدمة</p>
      </div>

      <div className="grid gap-4 w-full">
        {[
          { 
            title: "1. قبول الشروط", 
            text: "من خلال الوصول إلى GoldPulse واستخدامه، فإنك تقبل وتوافق على الالتزام بشروط الخدمة هذه. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام خدمتنا." 
          },
          { 
            title: "2. وصف الخدمة", 
            text: "يوفر GoldPulse مراقبة أسعار المعادن الثمينة في الوقت الفعلي، والتحليل الفني، ورؤى السوق المدعومة بالذكاء الاصطناعي، والمحتوى التعليمي. يتم تقديم خدمتنا لأغراض معلوماتية فقط ولا تشكل نصيحة مالية." 
          },
          { 
            title: "3. مسؤوليات المستخدم", 
            text: "أنت توافق على استخدام خدمتنا فقط للأغراض المشروعة ووفقاً لهذه الشروط. أنت مسؤول عن ضمان التزام استخدامك للخدمة بجميع القوانين واللوائح المعمول بها." 
          },
          { 
            title: "4. الملكية الفكرية", 
            text: "جميع المحتويات والميزات والوظائف في GoldPulse، بما في ذلك على سبيل المثال لا الحصر النصوص والرسومات والشعارات والبرمجيات، مملوكة لـ GoldPulse ومحمية بموجب قوانين الملكية الفكرية." 
          },
          { 
            title: "5. إخلاء مسؤولية الضمانات", 
            text: "يتم تقديم خدمتنا 'كما هي' و 'كما هي متوفرة' دون أي ضمانات من أي نوع، سواء كانت صريحة أو ضمنية. نحن لا نضمن أن الخدمة ستكون دون انقطاع أو آمنة أو خالية من الأخطاء." 
          },
          { 
            title: "6. تحديد المسؤولية", 
            text: "بأقصى حد يسمح به القانون، لن يكون GoldPulse مسؤولاً عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية ناتجة عن استخدامك للخدمة أو عدم قدرتك على استخدامها." 
          },
          { 
            title: "7. تغييرات الشروط", 
            text: "نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سنقوم بإخطار المستخدمين بالتغييرات المهمة عن طريق نشر إشعار على موقعنا. استمرارك في استخدام الخدمة بعد هذه التعديلات يشكل قبولاً للشروط المحدثة." 
          },
          { 
            title: "8. القانون الواجب التطبيق", 
            text: "تخضع هذه الشروط وتفسر وفقاً للقوانين المعمول بها، دون النظر إلى تعارض مبادئ القوانين." 
          }
        ].map((item, idx) => (
          <div key={idx} className="bg-white/[0.02] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.04] transition-all duration-300 text-right">
            <h3 className="text-primary font-black italic text-lg mb-3 uppercase tracking-tight">{item.title}</h3>
            <p className="text-muted-foreground/80 leading-relaxed text-base md:text-lg">{item.text}</p>
          </div>
        ))}
      </div>

      {/* زر العودة للرئيسية */}
      <div className="pt-10 flex justify-end">
        <button 
          onClick={() => { setView('home'); window.scrollTo({top:0, behavior:'smooth'}); }}
          className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.3em] hover:text-white transition-all"
        >
          <span>العودة للرئيسية</span>
          <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
)}
{/* محتوى صفحة إمكانية الوصول - Accessibility - مترجمة واحترافية */}
{view === 'accessibility' && (
  <div className="py-10 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full" dir="rtl">
    <div className="max-w-4xl mx-auto space-y-10 px-4 text-right font-sans">
      
      {/* العنوان الرئيسي */}
      <div className="border-r-4 border-primary pr-8 mb-16 text-right">
        <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
          إمكانية <span className="text-primary">الوصول</span>
        </h1>
        <p className="text-muted-foreground/60 text-[10px] font-black mt-3 uppercase tracking-[0.5em]">بيان إمكانية الوصول الرقمي</p>
      </div>

      <div className="grid gap-4 w-full">
        {[
          { 
            title: "التزامنا", 
            text: "يلتزم GoldPulse بضمان إمكانية الوصول الرقمي للأشخاص ذوي الاحتياجات الخاصة. نحن نحسن تجربة المستخدم باستمرار للجميع ونطبق معايير الوصول ذات الصلة." 
          },
          { 
            title: "ميزات إمكانية الوصول", 
            text: "لقد قمنا بتنفيذ الميزات التالية: دعم التنقل عبر لوحة المفاتيح، خيارات السمة الداكنة لراحة البصر، ألوان عالية التباين لسهولة القراءة، وهيكل HTML دلالي لقارئات الشاشة." 
          },
          { 
            title: "الامتثال للمعايير", 
            text: "نهدف إلى الالتزام بإرشادات الوصول إلى محتوى الويب (WCAG) 2.1 عند المستوى AA، والتي تشرح كيفية جعل محتوى الويب متاحاً بشكل أكبر." 
          },
          { 
            title: "القيود المعروفة", 
            text: "بينما نسعى جاهدين للوصول الكامل، قد تمثل الرسوم البيانية من TradingView وتحديثات الأسعار اللحظية بعض التحديات لقارئات الشاشة حالياً." 
          },
          { 
            title: "التقنيات المساعدة", 
            text: "تم تصميم موقعنا ليكون متوافقاً مع قارئات الشاشة (NVDA, JAWS, VoiceOver)، وبرامج تكبير الشاشة، وبرامج التعرف على الصوت." 
          },
          { 
            title: "ملاحظاتكم", 
            text: "نرحب بملاحظاتكم حول إمكانية الوصول. إذا واجهت أي عوائق، يرجى الاتصال بنا. نحن نأخذ ملاحظاتكم بجدية ونعمل على معالجة أي مشكلات فوراً." 
          }
        ].map((item, idx) => (
          <div key={idx} className="bg-white/[0.02] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.04] transition-all duration-300 text-right">
            <h3 className="text-primary font-black italic text-lg mb-3 uppercase tracking-tight">{item.title}</h3>
            <p className="text-muted-foreground/80 leading-relaxed text-base md:text-lg">{item.text}</p>
          </div>
        ))}
      </div>

      {/* زر العودة للرئيسية */}
      <div className="pt-10 flex justify-end">
        <button 
          onClick={() => { setView('home'); window.scrollTo({top:0, behavior:'smooth'}); }}
          className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.3em] hover:text-white transition-all"
        >
          <span>العودة للرئيسية</span>
          <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
)}
{/* محتوى صفحة اتصل بنا - Contact Us - احترافية وبسيطة */}
{view === 'contact' && (
  <div className="py-10 animate-in fade-in slide-in-from-bottom-5 duration-700 w-full font-sans" dir="rtl">
    <div className="max-w-5xl mx-auto px-4 text-right">
      
      {/* العنوان الرئيسي والسطر التعريفي */}
      <div className="border-r-4 border-primary pr-8 mb-16 text-right">
        <h1 className="text-4xl md:text-6xl font-black italic tracking-tighter text-white uppercase leading-none">
          أتصل <span className="text-primary">بنا</span>
        </h1>
        <p className="text-muted-foreground/80 text-sm md:text-lg font-medium mt-4 leading-relaxed max-w-2xl">
          لديك أسئلة، ملاحظات، أو تحتاج إلى مساعدة؟ يسعدنا دائماً السماع منك. إليك كيف يمكنك التواصل معنا:
        </p>
      </div>

      {/* المستطيلات المتوازنة - محاذاة لليمين */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 text-right">
        
        {/* صندوق البريد الإلكتروني */}
        <div className="bg-white/[0.02] border border-white/[0.05] p-10 rounded-2xl hover:border-primary/30 transition-all group flex flex-col items-start text-right">
          {/* الأيقونة والكلمة في نفس السطر */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              {/* أيقونة مغلف البريد */}
              <Mail size={24} className="text-primary" />
            </div>
            <h3 className="text-white font-black italic text-lg md:text-xl uppercase tracking-tight">الدعم الفني والبريد</h3>
          </div>
          
          {/* الوصف والبريد - محاذاة لليمين */}
          <p className="text-muted-foreground/70 text-base mb-0 leading-relaxed">للاستفسارات العامة والدعم الفني، يرجى مراسلتنا:</p>
          <a href="mailto:info@goldpulse.org" className="text-[#EAB308] font-mono font-black text-muted-foreground/70 text-base mb-0 leading-relaxed hover:underline tracking-tight">
            info@goldpulse.org
          </a>
        </div>

        {/* صندوق وقت الاستجابة */}
        <div className="bg-white/[0.02] border border-white/[0.05] p-10 rounded-2xl hover:border-primary/30 transition-all group flex flex-col items-start text-right">
          {/* الأيقونة والكلمة في نفس السطر */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Clock size={24} className="text-primary" />
            </div>
            <h3 className="text-white font-black italic text-lg md:text-xl uppercase tracking-tight">وقت الاستجابة</h3>
          </div>
          
          {/* الوصف والساعات - محاذاة لليمين */}
          <p className="text-muted-foreground/70 text-base mb-6 leading-relaxed">عادة ما نقوم بالرد خلال 24-48 ساعة عمل خلال أيام الأسبوع.</p>
        </div>
      </div>

      {/* نصائح قبل التواصل مع لينكات الصفحات */}
      <div className="bg-white/[0.01] border border-white/10 p-10 rounded-2xl relative overflow-hidden text-right shadow-2xl">
        <div className="absolute top-0 right-0 w-1 h-full bg-primary/30 animate-pulse"></div>
        <h4 className="text-white font-black mb-5 flex items-center gap-3 text-lg">
          <span className="w-2.5 h-2.5 rounded-full bg-primary"></span>
          قبل الاتصال بنا:
        </h4>
        <p className="text-muted-foreground/90 leading-relaxed text-base md:text-lg">
          يرجى مراجعة صفحات 
          <button onClick={() => setView('disclaimer')} className="text-primary mx-1.5 hover:underline font-bold transition-colors">إخلاء المسؤولية</button> 
          و
          <button onClick={() => setView('risk')} className="text-primary mx-1.5 hover:underline font-bold transition-colors">تحذير المخاطر</button> 
          للحصول على معلومات هامة. بالنسبة للمشكلات الفنية، يرجى محاولة تحديث الصفحة أو مسح ذاكرة التخزين المؤقت للمتصفح.
        </p>
      </div>

      {/* زر العودة للرئيسية */}
      <div className="pt-16 flex justify-end">
        <button 
          onClick={() => { setView('home'); window.scrollTo({top:0, behavior:'smooth'}); }}
          className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] md:text-xs tracking-[0.3em] hover:text-white transition-all"
        >
          <span>العودة للرئيسية</span>
          <ArrowRightLeft size={16} className="rotate-180 group-hover:translate-x-5 transition-transform" />
        </button>
      </div>
    </div>
  </div>
)}
{/* محتوى صفحة أسعار الفضة - Silver Page */}
{view === 'silver' && (
  <div className="py-10 animate-in fade-in duration-700 w-full font-sans" dir="rtl">
    <div className="max-w-7xl mx-auto px-4">
      
      {/* المربع الأول: السعر العالمي والرسم البياني */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1">
          <div className="bg-[#111] border border-white/5 p-8 rounded-[32px] h-full flex flex-col justify-between">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-slate-400/20 flex items-center justify-center shadow-[0_0_15px_rgba(148,163,184,0.2)]">
                <div className="w-6 h-6 rounded-full bg-slate-400"></div>
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white italic">سعر الفضة (XAG)</h2>
              <span className="text-green-500 text-2xl animate-pulse">▲</span>
            </div>
            
            <div className="space-y-4">
              <p className="text-5xl md:text-6xl font-black text-white font-mono tracking-tighter">
                ${prices.silver}
              </p>
              <div className="flex items-center gap-3 text-lg font-bold">
                <span className="text-green-500">+1.45%</span>
                <span className="text-muted-foreground font-mono">+$0.42</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8 border-t border-white/5 pt-6 font-mono">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase font-black">أعلى سعر اليوم</p>
                <p className="text-white font-bold">{prices.silverHigh}</p>
              </div>
              <div className="text-left">
                <p className="text-[10px] text-muted-foreground uppercase font-black">أقل سعر اليوم</p>
                <p className="text-white font-bold">{prices.silverLow}</p>
              </div>
            </div>
          </div>
        </div>

        {/* الرسم البياني الصغير للفضة */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/5 rounded-[32px] p-2 h-[400px] overflow-hidden">
          <GoldChart symbol="OANDA:XAGUSD" />
        </div>
      </div>

      {/* المربع الثاني: سعر الفضة بجميع العملات */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6 pr-2">
          <div className="w-2 h-8 bg-slate-400 rounded-full"></div>
          <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">سعر الفضة بمختلف العملات</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "الدولار الأمريكي", code: "USD", val: prices.silver },
            { label: "اليورو الأوروبي", code: "EUR", val: (prices.silver * 0.92).toFixed(2) },
            { label: "الجنيه المصري", code: "EGP", val: (prices.silver * usdToEgp).toFixed(2) },
            { label: "الريال السعودي", code: "SAR", val: (prices.silver * 3.75).toFixed(2) },
            { label: "الدرهم الإماراتي", code: "AED", val: (prices.silver * 3.67).toFixed(2) },
            { label: "الدينار الكويتي", code: "KWD", val: (prices.silver * 0.31).toFixed(2) },
            { label: "الريال القطري", code: "QAR", val: (prices.silver * 3.64).toFixed(2) },
            { label: "الليرة التركية", code: "TRY", val: (prices.silver * 32.5).toFixed(2) },
            { label: "الجنيه الاسترليني", code: "GBP", val: (prices.silver * 0.79).toFixed(2) }
          ].map((item, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 p-6 rounded-2xl flex items-center justify-between hover:bg-white/[0.05] transition-all">
              <div className="text-right">
                <p className="text-[10px] text-slate-400 font-black mb-1 uppercase tracking-widest">{item.label}</p>
                <p className="text-xl font-mono font-black text-white">{item.val}</p>
              </div>
              <span className="text-xs font-black text-slate-500 italic">{item.code}</span>
            </div>
          ))}
        </div>
      </div>

      {/* المربع الثالث: عن الفضة */}
      <div className="bg-white/[0.02] border border-white/5 p-10 rounded-[40px] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-slate-400/30 to-transparent"></div>
        <h2 className="text-3xl font-black text-white italic mb-8 border-r-4 border-slate-400 pr-6 uppercase tracking-tighter">عن معدن الفضة (About Silver)</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <p className="text-muted-foreground leading-relaxed text-lg">
            تعتبر الفضة (XAG) معدناً ثميناً يُقدر لاستخداماته الصناعية وجاذبيته الاستثمارية على حد سواء. غالباً ما تُلقب بـ <span className="text-slate-300 font-bold">"ذهب الرجل الفقير"</span>، وتوفر الفضة نقطة دخول سهلة للاستثمار في المعادن الثمينة مع الحفاظ على طلب صناعي قوي في الإلكترونيات، والألواح الشمسية، والمعدات الطبية.
          </p>
          <p className="text-muted-foreground leading-relaxed text-lg">
            تُسعر الفضة عادةً بالدولار الأمريكي للأونصة (troy ounce) وتتأثر بالطلب الصناعي، والطلب الاستثماري، وإنتاج المناجم، والعوامل الاقتصادية الأوسع. وتعد <span className="text-slate-300 font-bold">نسبة الذهب إلى الفضة</span> مقياساً شائعاً يستخدم لتقييم القيمة النسبية بين المعدنين.
          </p>
        </div>
      </div>

      {/* زر العودة */}
      <div className="pt-12 flex justify-start">
        <button onClick={() => setView('home')} className="group flex items-center gap-4 text-slate-400 font-black uppercase text-[11px] tracking-[0.4em] hover:text-white transition-all">
          العودة للرئيسية <ArrowRightLeft size={16} className="rotate-180" />
        </button>
      </div>

    </div>
  </div>
)}
{/* محتوى صفحة أسعار الذهب - Gold Page */}
{view === 'gold' && (
  <div className="py-10 animate-in fade-in duration-700 w-full font-sans" dir="rtl">
    <div className="max-w-7xl mx-auto px-4">
      
      {/* المربع الأول: السعر العالمي والرسم البياني */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1">
          <div className="bg-[#111] border border-primary/10 p-8 rounded-[32px] h-full flex flex-col justify-between relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
            
            <div className="flex items-center gap-4 mb-6 relative">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center shadow-[0_0_15px_rgba(234,179,8,0.2)]">
                <div className="w-6 h-6 rounded-full bg-primary shadow-[0_0_10px_#eab308]"></div>
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white italic tracking-tighter uppercase">سعر الذهب (XAU)</h2>
              <span className="text-green-500 text-2xl animate-pulse">▲</span>
            </div>
            
            <div className="space-y-4 relative">
              <p className="text-5xl md:text-6xl font-black text-white font-mono tracking-tighter">
                ${prices.gold}
              </p>
              <div className="flex items-center gap-3 text-lg font-bold">
                <span className="text-green-500">{prices.change}</span>
                <span className="text-muted-foreground font-mono">{prices.amount}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8 border-t border-white/5 pt-6 font-mono relative">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase font-black">أعلى سعر اليوم</p>
                <p className="text-primary font-bold">{prices.goldHigh}</p>
              </div>
              <div className="text-left">
                <p className="text-[10px] text-muted-foreground uppercase font-black">أقل سعر اليوم</p>
                <p className="text-primary font-bold">{prices.goldLow}</p>
              </div>
            </div>
          </div>
        </div>

        {/* الرسم البياني للذهب */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/5 rounded-[32px] p-2 h-[400px] overflow-hidden group hover:border-primary/20 transition-all">
          <GoldChart symbol="OANDA:XAUUSD" />
        </div>
      </div>

      {/* المربع الثاني: سعر الذهب بجميع العملات (3 في كل سطر) */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6 pr-2">
          <div className="w-2 h-8 bg-primary rounded-full shadow-[0_0_10px_#eab308]"></div>
          <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">سعر الذهب بمختلف العملات</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "الدولار الأمريكي", code: "USD", val: prices.gold },
            { label: "اليورو الأوروبي", code: "EUR", val: (prices.gold * 0.92).toFixed(2) },
            { label: "الجنيه المصري (أونصة)", code: "EGP", val: (prices.gold * usdToEgp).toFixed(2) },
            { label: "الريال السعودي", code: "SAR", val: (prices.gold * 3.75).toFixed(2) },
            { label: "الدرهم الإماراتي", code: "AED", val: (prices.gold * 3.67).toFixed(2) },
            { label: "الدينار الكويتي", code: "KWD", val: (prices.gold * 0.31).toFixed(2) },
            { label: "الريال القطري", code: "QAR", val: (prices.gold * 3.64).toFixed(2) },
            { label: "الليرة التركية", code: "TRY", val: (prices.gold * 32.5).toFixed(2) },
            { label: "الجنيه الاسترليني", code: "GBP", val: (prices.gold * 0.79).toFixed(2) }
          ].map((item, idx) => (
            <div key={idx} className="bg-white/[0.02] border border-white/5 p-6 rounded-2xl flex items-center justify-between hover:border-primary/30 hover:bg-white/[0.04] transition-all group">
              <div className="text-right">
                <p className="text-[10px] text-muted-foreground font-black mb-1 uppercase tracking-widest group-hover:text-primary transition-colors">{item.label}</p>
                <p className="text-xl font-mono font-black text-white">{item.val}</p>
              </div>
              <span className="text-xs font-black text-primary/40 italic group-hover:text-primary transition-colors">{item.code}</span>
            </div>
          ))}
        </div>
      </div>

      {/* المربع الثالث: عن الذهب */}
      <div className="bg-white/[0.02] border border-white/5 p-10 rounded-[40px] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-transparent via-primary/40 to-transparent"></div>
        <h2 className="text-3xl font-black text-white italic mb-8 border-r-4 border-primary pr-6 uppercase tracking-tighter">عن الذهب (About Gold)</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <p className="text-muted-foreground leading-relaxed text-lg">
            يُعد الذهب <span className="text-primary font-bold">(XAU)</span> الملاذ الآمن الأكثر شهرة في العالم والأصل المفضل للتحوط ضد التضخم وتقلبات العملات. يتميز الذهب بندرته وقيمته الذاتية التي لا تعتمد على أي جهة إصدار، مما يجعله مخزناً مثالياً للقيمة عبر العصور.
          </p>
          <p className="text-muted-foreground leading-relaxed text-lg">
            تتأثر أسعار الذهب بشكل مباشر بقرارات البنوك المركزية، وأسعار الفائدة، والتوترات الجيوسياسية. يتم تسعير الذهب عالمياً بـ <span className="text-primary font-bold">الأونصة تروي</span>، ويُراقب المستثمرون تحركاته بدقة كونه مؤشراً رئيسياً لصحة الاقتصاد العالمي ومستوى عدم اليقين في الأسواق.
          </p>
        </div>
      </div>

      {/* زر العودة */}
      <div className="pt-12 flex justify-start">
        <button onClick={() => setView('home')} className="group flex items-center gap-4 text-primary font-black uppercase text-[11px] tracking-[0.4em] hover:text-white transition-all">
          العودة للرئيسية <ArrowRightLeft size={16} className="rotate-180" />
        </button>
      </div>

    </div>
  </div>
)}
{/* 2. الفوتر (تم تقليل المسافات الداخلية) */}
<footer className="pt-8 text-center relative">
    {/* الروابط القانونية */}
<div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-6 text-[10px] md:text-xs font-bold text-muted-foreground uppercase tracking-wider">
    
    {/* زرار الرئيسية عشان لو حد تاه تحت */}
    <button 
      onClick={() => { setView('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
      className={`transition-colors ${view === 'home' ? 'text-primary' : 'hover:text-primary'}`}
    >
      الرئيسية
    </button>
    {/* زرار تحذير المخاطر المعدل */}
    <button 
      onClick={() => { setView('risk'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
      className={`transition-colors ${view === 'risk' ? 'text-primary' : 'hover:text-primary'}`}
    >
      تحذير المخاطر
    </button>

    <button onClick={() => { setView('disclaimer'); window.scrollTo(0,0); }}>إخلاء المسؤولية</button>
    <button onClick={() => { setView('terms'); window.scrollTo(0,0); }}>الشروط</button>
<button 
  onClick={() => { setView('privacy'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
  className={`hover:text-primary transition-colors ${view === 'privacy' ? 'text-primary underline' : ''}`}
>
  الخصوصية
</button>
    <button onClick={() => { setView('accessibility'); window.scrollTo(0,0); }}>إمكانية الوصول</button>
<button 
  onClick={() => { setView('contact'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
  className={`transition-colors uppercase ${view === 'contact' ? 'text-primary underline underline-offset-4' : 'hover:text-primary'}`}
>
  اتصل بنا
</button>
</div>

    {/* نص إخلاء المسؤولية (تقليل المسافات mb-4 بدل mb-12) */}
    <div className="max-w-4xl mx-auto px-6 mb-0 space-y-2">
        <p className="text-[10px] md:text-[11px] text-muted-foreground/50 leading-relaxed max-w-3xl mx-auto">
            الأسعار مقدمة لأغراض إعلامية فقط. قد تختلف معدلات التحديث. 
            التداول ينطوي على مخاطر. استشر دائماً مستشاراً مالياً مؤهلاً قبل اتخاذ قرارات الاستثمار.
            <span className="block mt-1 font-bold text-muted-foreground/70 tracking-tight">هذه ليست نصيحة مالية.</span>
        </p>
    </div>
{/* صف أيقونات السوشيال ميديا - نسخة مصغرة ومدمجة */}
<div className="flex justify-center items-center gap-4 mt-0 mb-0 w-full py-4" dir="ltr">
  {[
    { 
      id: 'FB',
      url: 'https://www.facebook.com/goldpulse/', 
      path: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
    },
    { 
      id: 'IG',
      url: 'https://www.instagram.com/goldpulseorg/', 
      path: (
        <>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
        </>
      )
    },
    { 
      id: 'TK',
      url: 'https://www.tiktok.com/@goldpulse.org', 
      path: <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path>
    },
    { 
      id: 'YT',
      url: 'https://www.youtube.com/@goldpulseorg', 
      path: (
        <>
          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.42a2.78 2.78 0 0 0-1.94 2C1 8.14 1 12 1 12s0 3.86.46 5.58a2.78 2.78 0 0 0 1.94 2c1.72.42 8.6.42 8.6.42s6.88 0 8.6-.42a2.78 2.78 0 0 0 1.94-2C23 15.86 23 12 23 12s0-3.86-.46-5.58z"></path>
          <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"></polygon>
        </>
      )
    }
  ].map((social) => (
    <a 
      key={social.id}
      href={social.url}
      target="_blank"
      rel="noopener noreferrer"
      className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-primary hover:bg-primary hover:text-black hover:shadow-[0_0_15px_rgba(234,179,8,0.3)] hover:-translate-y-1 transition-all duration-500 group"
    >
      <svg 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className="w-5 h-5 group-hover:scale-110 transition-transform"
      >
        {social.path}
      </svg>
    </a>
  ))}
</div>
    {/* اللوجو وحقوق الملكية (تقليل المسافة العلوية) */}
    <div className="pb-6">
        <div className="flex items-center justify-center gap-3 mb-2 opacity-20">
            <div className="h-px w-6 bg-muted-foreground"></div>
            <div className="text-lg font-black italic tracking-tighter text-muted-foreground uppercase">
                GOLD<span className="text-primary/50">PULSE</span>
            </div>
            <div className="h-px w-6 bg-muted-foreground"></div>
        </div>
        <p className="text-[9px] text-muted-foreground/20 uppercase tracking-[0.3em] font-black italic">
            © 2026 GoldPulse. جميع الحقوق محفوظة.
        </p>
    </div>
</footer>
      </div>
    </div>
  );
}

export default App;